import _ from 'lodash'
import {organizationPermissionCodes, permissionCodes} from 'modules/permissions/contants'
import PermissionContext from 'modules/permissions/PermissionContext'
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react'
import {connect} from 'react-redux'
import {NavLink, Redirect, useLocation, useRouteMatch} from 'react-router-dom'
import {branch, compose, nest} from 'recompose'
import {getLogin} from 'redux/selectors'
import {staticPaths} from 'routes/staticPaths'
import {createCheckPermissions, hasPermissions} from 'views/AppLocals/factory/createCheckPermissions'
import {NullAsDefault, renderIf} from 'views/Shared'
import WorkspaceActivities from './Activities/WorkspaceActivities'
import WorkspaceFeatures from './components/WorkspaceFeatures'
import WorkspaceExamination from './Examination/WorkspaceExamination'
import WorkspaceHome from './HomePage/WorkspaceHome'
import WorkspaceSetting from './Settings/WorkspaceSetting'
import WorkspaceContext from './WorkspaceContext'
import {WorkspaceProvider} from './WorkspaceProvider'
import WorkspaceLandingPage from './WorkspaceLandingPage'
import WorkspaceMembers from './Host/WorkspaceMembers'
import WorkspaceCourse from './Course/WorkspaceCourse'
import WorkspaceFamilyBook from './FamilyBook/WorkspaceFamilyBook'
import LoadingBlock from '../Loading/LoadingBlock'
import WorkspaceShop from './Shop/WorkspaceShop'
import WorkspaceFinance from './Finance/WorkspaceFinance'
import ContentLayout from '../../components/layouts/Default/ContentLayout'
import FlyWrapper from '../AppLocals/Templates/FlyWrapper'
import useTranslate from '../../modules/local/useTranslate'
import {SearchGroupIcon} from '../../components/AppIcon'
import classNames from 'classnames'
import {Avatar} from 'antd'
import {BiSolidChurch} from 'react-icons/bi'
import {BsChevronDown, BsChevronRight, BsGrid} from 'react-icons/bs'
import DrawDialog from '../../components/drawer/dialog/DrawDialog'
import AdminOrganizations from './components/AdminOrganizations'
import getTitle from '../../helpers/getTitle'
import {useAppConfig} from '../../modules/local/AppConfigContext'
import {getId} from '../../apis/model/base'
import UnderDevelopment from '../Shared/components/UnderDevelopment'
import {PendingEnrollmentsBadge, PendingHouseholdsBadge} from './components/WorkspaceBadges'
import {HiLockClosed} from "react-icons/hi2";
import Translate from "../../modules/local/Translate";
import TooltipApp from "../../components/TooltipApp";
import {ComingSoonBadge, filterDevItems, WorkspaceSidebarSubItem} from './components/WorkspaceSidebarSubItem'

// ─── Route keys ──────────────────────────────────────────────────────────────

export const WorkSpaceActions = Object.freeze({
    HOME: 'dashboard',
    COMMUNICATIONS: 'activities',
    MEMBER: 'member',
    GAMES: 'games',
    COURSE: 'course',
    PRODUCT: 'product',
    FINANCE: 'finance',
    FAMILY_BOOK: 'family_books',
    SETTING: 'setting',
})

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Route nào tương ứng "module lớn" owner tự bật/tắt (xem config/organization_features.php,
// Modules/Organization/Http/Controllers/OrganizationFeatureController.php) - route KHÔNG có
// mặt ở đây (dashboard, activities, member, setting) luôn hiện, chỉ disable theo permission
// như trước giờ. Route CÓ mặt ở đây: ẨN HẲN khỏi sidebar khi module đang tắt cho org - khác
// với "có quyền hay không" (thành viên thiếu role permission vẫn thấy mục ở trạng thái khoá
// khi module đang BẬT). Trước đây GAMES/PRODUCT tự gate riêng qua service_type bitmask
// (isServiceDisabled) - đã hợp nhất về đây, 1 nguồn sự thật duy nhất với BE.
const FEATURE_KEY_BY_ROUTE = {
    [WorkSpaceActions.COURSE]: 'course',
    [WorkSpaceActions.FAMILY_BOOK]: 'family_book',
    [WorkSpaceActions.FINANCE]: 'finance',
    [WorkSpaceActions.GAMES]: 'game',
    [WorkSpaceActions.PRODUCT]: 'product',
}

const isModuleEnabledForRoute = (route, organization) => {
    const featureKey = FEATURE_KEY_BY_ROUTE[route.key]
    if (!featureKey) return true
    return (organization?.enabled_module_features || []).includes(featureKey)
}

// ─── Route definitions ───────────────────────────────────────────────────────

const routeDefinitions = [
    {
        key: WorkSpaceActions.HOME,
        label: 'overview',
        component: WorkspaceHome,
    },
    {
        key: WorkSpaceActions.COMMUNICATIONS,
        label: 'communications',
        component: WorkspaceActivities,
    },
    {
        key: WorkSpaceActions.MEMBER,
        label: 'members & organization',
        component: WorkspaceMembers,
        required_permissions: [organizationPermissionCodes.organizationManageMember],
    },
    {
        key: WorkSpaceActions.COURSE,
        label: 'training & education',
        component: WorkspaceCourse,
        required_permissions: [organizationPermissionCodes.organizationManageCourse],
    },
    {
        key: WorkSpaceActions.FAMILY_BOOK,
        label: 'records & sacraments',
        // WorkspaceFamilyBook (Kristo Family, thay catholic_household*) - key route đã đổi từ
        // 'households' cũ sang 'family_books' cho khớp tên xuyên suốt code (model/API/URL đều gọi
        // family_book, chỉ riêng key route còn sót "households" từ thời catholic_household).
        // WorkspaceCatholicHousehold cũ vẫn còn trong codebase (không xoá) nhưng không còn liên kết
        // từ menu chính nữa.
        component: WorkspaceFamilyBook,
        isOptional: true,
        required_permissions: [organizationPermissionCodes.organizationManageSacrament],
    },
    {
        key: WorkSpaceActions.FINANCE,
        label: 'finance & charity support',
        component: WorkspaceFinance,
        isOptional: true,
        required_permissions: [organizationPermissionCodes.organizationManageFinance],
        isUnderDevelopment: true,
    },
    {
        key: WorkSpaceActions.GAMES,
        label: 'games',
        component: WorkspaceExamination,
        isOptional: true,
        // Kỳ thi + Kristo Games gộp chung 1 quyền duy nhất (organization_manage_games) -
        // không tách quyền riêng cho từng sub-item dù schema BE có key manage_examination
        // riêng, tránh 1 menu phải hiểu 2 permission khác nhau.
        required_permissions: [organizationPermissionCodes.organizationManageGames],
    },
    {
        key: WorkSpaceActions.SETTING,
        label: 'system settings',
        component: WorkspaceSetting,
        required_permissions: [organizationPermissionCodes.organizationManageInfo],
    },
    {
        key: WorkSpaceActions.PRODUCT,
        label: 'shops',
        component: WorkspaceShop,
        isOptional: true,
        required_permissions: [organizationPermissionCodes.organizationManageProduct],
    },
]

export const routes = routeDefinitions.map(
    ({key, required_permissions = [], component, isUnderDevelopment, ...rest}) => ({
        key,
        ...rest,
        isUnderDevelopment,
        required_permissions,
        component: isUnderDevelopment
            ? () => <UnderDevelopment/>
            : required_permissions.length
                ? createCheckPermissions(required_permissions)(component)
                : component,
    })
)

// ─── Sub-menu: Home ──────────────────────────────────────────────────────────

// "notifications" (Thông báo) đã bỏ khỏi menu - trùng với Truyền thông > Thông báo (WorkspaceActivities
// AnnouncementsTab), nay đã có đủ 2 tab Cộng đoàn/Khóa học ở đó. Component WorkspaceNotifications
// giữ nguyên trong codebase (không xoá), chỉ không còn liên kết từ menu chính - theo đúng tiền lệ
// đã áp dụng cho catholic-household cũ (xem comment ở FAMILY_BOOK_SUB_ITEMS phía dưới).
export const HOME_SUB_ITEMS = [
    {key: 'summary-board', label: 'summary board'},
    // {key: 'activity-calendar', label: 'activity calendar'},
    // {key: 'recent-activities', label: 'recent activities', isUnderDevelopment: true},
]

// ─── Sub-menu: Communications ────────────────────────────────────────────────

export const COMMUNICATIONS_SUB_ITEMS = [
    {
        key: 'announcements', label: 'announcements',
        required_permissions: [organizationPermissionCodes.organizationManageAnnouncement]
    },
    // { key: 'articles',         label: 'articles',        isUnderDevelopment: true },
    {key: 'events', label: 'events', required_permissions: [organizationPermissionCodes.organizationManageEvent]},
    {
        key: 'channels',
        label: 'content channels',
        required_permissions: [organizationPermissionCodes.organizationManageChannel]
    },
    {key: 'activity-schedule', label: 'activity schedule'},
    // { key: 'media-library',    label: 'media library',   isUnderDevelopment: true },
    {
        key: 'moderation',
        label: 'moderation',
        required_permissions: [organizationPermissionCodes.organizationManageChannel]
    },
]

// ─── Sub-menu: Members & Organization ────────────────────────────────────────

export const MEMBER_SUB_ITEMS = [
    {key: 'dashboard', label: 'overview'},
    {
        key: 'org-members', label: 'member list',
        required_permissions: [organizationPermissionCodes.organizationManageMember],
    },
    {key: 'personal-profile', label: 'personal profile'},
    {
        key: 'groups-units', label: 'groups & units',
        required_permissions: [organizationPermissionCodes.organizationManageGroup],
        children: [
            {key: 'org-groups', label: 'group list'},
            {key: 'org-tree', label: 'organization tree'},
        ],
    },
    {
        key: 'org-relations', label: 'organization relations',
        required_permissions: [organizationPermissionCodes.organizationManageMember],
        children: [
            {key: 'governing_organizations', label: 'governing organizations'},
            {key: 'subordinate_units', label: 'subordinate units'},
            {key: 'peer-members', label: 'affiliated members'},
        ],
    },
    // {
    //     key: 'stats-classification', label: 'statistics & classification',
    //     children: [
    //         { key: 'member-anniversaries', label: 'member anniversaries', isUnderDevelopment: true },
    //         { key: 'age-group',            label: 'age group',            isUnderDevelopment: true },
    //         { key: 'family-situation',     label: 'family situation',     isUnderDevelopment: true },
    //     ],
    // },
    // { key: 'related-events', label: 'related events' },
]

// ─── Sub-menu: Games (Examination) ───────────────────────────────────────────
// Gom 3 mục kỳ thi (danh sách/bài dự thi/ngân hàng câu hỏi) vào 1 group "Kỳ thi" - Kristo Games
// (tính năng khác hẳn, không thuộc kỳ thi) tách riêng đứng ngang hàng, không lồng chung.

export const GAMES_SUB_ITEMS = [
    {
        key: 'examination-group', label: 'examinations',
        children: [
            {key: 'examination', label: 'examination list'},
            {key: 'examination-records', label: 'exam submissions'},
            {key: 'examination-questions', label: 'question bank'},
        ],
    },
    {key: 'kristo-games', label: 'kristo games'},
    // {
    //     key: 'report', label: 'report',
    //     children: [
    //         {key: 'exam-ranking-report', label: 'ranking report'},
    //         {key: 'exam-report-group-by-infos', label: 'report by group'},
    //     ],
    // },
]

// ─── Sub-menu: Training & Education ─────────────────────────────────────────

export const COURSE_SUB_ITEMS = [
    // Nhóm "Tổng quan & Giới thiệu" đứng đầu - gộp Tổng quan (vận hành hằng ngày: số liệu, tuyển
    // sinh, lịch, thông báo) với Giới thiệu (mô hình dữ liệu/vòng đời/phân quyền của cả phân hệ,
    // CourseIntroduction.js - nội dung TOÀN CỤC, không thuộc riêng nhóm "Quản lý đào tạo" nào bên
    // dưới nên KHÔNG lồng vào đó). Trước đây "Tổng quan" là 1 mục cấp 1 đứng riêng; gộp thành group
    // 2 con thay vì thêm hẳn 1 mục cấp 1 mới cho Giới thiệu - vì cây này dùng chung layout với
    // CoursePortalTopNav.js (top-nav access-portal, hàng ngang chia đều theo SỐ MỤC CẤP 1), thêm 1
    // mục cấp 1 nữa sẽ làm hàng tab đó chật hơn trên mobile. key/label 'dashboard' KHÔNG đổi (dùng
    // chung nhiều nơi khác trong app) - vẫn giữ nguyên là lá con, chỉ đổi cấp lồng.
    {
        key: 'overview-and-intro-group', label: 'overview and introduction',
        children: [
            {key: 'dashboard', label: 'training overview and reports'},
            {key: 'introduction', label: 'training introduction'},
        ],
    },
    // "Quản lý đào tạo" đứng NGAY SAU Tổng quan (trước cả Khóa & Lớp học/Nhân sự) - đây là nhóm
    // thiết lập nền (chương trình học/môn học/mẫu chứng chỉ/ghi danh), làm trước khi vận hành khóa
    // & lớp nên ưu tiên vị trí cao hơn. (Trước đây khối này bị dán lặp 2 lần y hệt ngay bên dưới,
    // khiến sidebar hiện "Quản lý đào tạo" 2 lần - dọn còn 1.)
    {
        key: 'overview-group', label: 'training management',
        children: [
            // { key: 'training-report', label: 'training report',  isUnderDevelopment: true },
            {key: 'curriculum', label: 'curriculum'},
            {key: 'subject', label: 'subjects'},
            // label KHÔNG dùng lại key 'certificates' (dùng chung ở CourseInfoTab.js, phạm vi
            // khác) - key riêng 'course certificate templates' cho đúng mục sidebar này.
            {key: 'course-medals', label: 'course certificate templates'},
            {key: 'register-for-course', label: 'enrollments', badge: <PendingEnrollmentsBadge />},
        ],
    },

    // Khóa học + Lớp học - từng gộp 1 group ("course-classes-group"), sau rã phẳng thành 3 mục cấp 1,
    // giờ GOM LẠI đúng 1 group như cũ (bạn yêu cầu) - dùng lại y hệt key/label/children đã có sẵn ở
    // group "course-classes-group" của CoursePortalTopNav.js (top-nav access-portal) để 2 cây điều
    // hướng nói cùng 1 khái niệm giống hệt nhau, không tạo key/bản dịch song song.
    {
        key: 'course-classes-group', label: 'course and class management',
        children: [
            // "Điều hành Khóa & Lớp học" - màn hình vận hành hằng ngày (chọn khóa đang diễn ra rồi
            // chọn lớp thuộc khóa, xem CourseClassOperations.js).
            {key: 'course-class-operations', label: 'course and class operations'},
            // "Danh sách Khóa học"/"Danh sách Lớp học" - 2 màn TRA CỨU/thư viện, liệt kê MỌI khóa/lớp
            // bất kể trạng thái (khác "Điều hành..." ở trên chỉ lấy khóa đang diễn ra).
            {key: 'course', label: 'course lookup'},
            {key: 'course-class', label: 'class lookup'},
        ],
    },
    // "Thời khóa biểu" (course-class-timetable) - bỏ khỏi menu (bạn yêu cầu): lịch trình cả khóa
    // giờ đã nhúng thẳng trong "Điều hành Khóa & Lớp học" (CourseSchedulePanel.js,
    // CourseClassOperations.js), không cần mục menu riêng trùng lặp nữa. Route/component
    // CourseClassTimetable.js GIỮ NGUYÊN (không xoá) - chỉ ẩn khỏi menu.

    {
        key: 'training-staff-group', label: 'personnel management',
        children: [
            {key: 'student', label: 'students'},
            {key: 'teacher', label: 'instructors'},
            {key: 'parents', label: 'parents'},
            // Xác định danh tính (khớp học viên mới ghi danh với hồ sơ có sẵn) + Học viên trùng lặp
            // (gộp hồ sơ trùng) - cùng bản chất "đối soát danh tính học viên", gộp 1 group.
            {
                key: 'identity-integrity-group', label: 'identity verification and duplicates',
                children: [
                    {key: 'identity-resolution', label: 'identity resolution'},
                    {key: 'duplicate-students', label: 'duplicate students'},
                ],
            },
        ],
    },
    // Mục cấp 1 riêng (không lồng trong group nào) - đúng vị trí "Xuất/Nhập hàng loạt" của Sổ Gia
    // Đình (FAMILY_BOOK_SUB_ITEMS bên dưới, ngang hàng Tổng quan/Sổ Gia Đình/Giáo Dân): gom hết các
    // loại Xuất/Nhập hàng loạt của phân hệ Khóa học vào 1 màn hình, nhìn là biết đủ loại. Đợt đầu chỉ
    // có tab Học viên (xem CourseImportExportPage.js/course_import_export_config.js) - KHÔNG thay thế
    // 2 nút "Nhập/Xuất dữ liệu học viên" cũ trong context-menu từng Khóa (Courses.js, luồng single-
    // shot khác hẳn, giữ nguyên song song).
    {key: 'course-bulk-import-export', label: 'course bulk import export title'},
]

// ─── Sub-menu: Shop ──────────────────────────────────────────────────────────

export const SHOP_SUB_ITEMS = []

// ─── Sub-menu: Finance ───────────────────────────────────────────────────────

export const FINANCE_SUB_ITEMS = [
    {key: 'dashboard', label: 'financial overview', isUnderDevelopment: true},
    {key: 'income', label: 'income & contributions', isUnderDevelopment: true},
    {key: 'outcome', label: 'expenses', isUnderDevelopment: true},
    {key: 'fund-budget', label: 'fund & budget', isUnderDevelopment: true},
    {key: 'charity-support', label: 'charity & support', isUnderDevelopment: true},
    {key: 'finance-report', label: 'financial report', isUnderDevelopment: true},
]

// ─── Sub-menu: Records & Sacraments (Kristo Family) ──────────────────────────
// key phải khớp với key WorkspaceFamilyBook đọc qua ?rel= (xem useRouteSearchParam('rel') trong
// component đó). Đổi hết catholic-household-* -> family-book-* theo yêu cầu, thêm cả Xức Dầu Bệnh
// Nhân (thiếu ở bản cũ).
//
// 4 nhóm theo HÀNH VI (không theo cấu trúc dữ liệu): (1) Tổng Quan - bức tranh chung + quyền/nghĩa
// vụ + lối vào access-portal; (2) Giáo Dân - "tìm 1 người"; (3) Sổ Gia Đình - đơn vị "hộ gia đình";
// (4) Nhập liệu hàng loạt.
//
// "Giáo Dân" GỘP THẲNG vào member-lookup (KHÔNG còn group con "Tra cứu các bí tích" 6 mục như bản
// trước) - ParishionerLookupPage giờ tự có sẵn filter chip theo bí tích (MemberFilterPanel,
// showSacramentFilter mặc định true) VÀ tab "Bí tích cử hành tại giáo xứ" (LOOKUP_SCOPES.CELEBRATED,
// căn theo family_book_member_sacraments.parish_id - xem comment ở WorkspaceFamilyBook.js), nên tra
// theo tên hay theo bí tích giờ chỉ còn 1 màn hình duy nhất, đúng thực tế "vẫn là tìm 1 người".
export const FAMILY_BOOK_SUB_ITEMS = [
    {key: 'family-book-overview', label: 'overview'},
    {key: 'family-books', label: 'family record books', badge: <PendingHouseholdsBadge/>},
    // Tra cứu 1 giáo dân bất kỳ theo tên, trên TOÀN BỘ sổ thuộc giáo xứ - xem WorkspaceFamilyBook.js
    // (ParishionerLookupPage) + FamilyBookMemberController@fetchFamilyBookMembersByParish (BE).
    {key: 'family-book-member-lookup', label: 'parishioners'},
    // "Xuất/Nhập hàng loạt" GENERIC (Sổ/Thành viên/Bí tích, xem FamilyBookImportExportPage.js +
    // import_export_config.js) - ĐỘC LẬP với GEDCOM (.ged, xem GedcomImportCard - nhập cả cây phả hệ
    // nhiều đời, phạm vi khác hẳn). Thay HẲN luồng "Nhập File Sổ Gia Đình" Excel cũ.
    {key: 'family-book-bulk-import-export', label: 'bulk import export title'},
]

// ─── Sub-menu: Settings ──────────────────────────────────────────────────────
// key phải khớp với path trong routeEntities (dùng cho ?rel=key)

export const SETTING_SUB_ITEMS = [
    {key: 'overview', label: 'general settings'},
    {
        key: 'profiles',
        label: 'organization information',
        children: [
            {key: 'about-us', label: 'about us'},
            {key: 'photos', label: 'photos'},
            {key: 'profile', label: 'more information'},
            {key: 'categories', label: 'categories'},
            {key: 'organization_verification', label: 'organization verification'},
            {
                key: 'location-contact',
                label: 'location & contact',
                children: [
                    {key: 'location', label: 'location'},
                    {key: 'contact', label: 'contact'},
                ],
            },
        ],
    },
    {
        key: 'permissions-group',
        label: 'permissions & roles',
        required_permissions: [organizationPermissionCodes.organizationManageRole],
        children: [
            {key: 'entity-role', label: 'roles & positions'},
            {key: 'permission-role', label: 'access permissions'},
            {key: 'feature-access-grants', label: 'temporary access grants'},
        ],
    },
    {
        key: 'feature-toggle',
        label: 'enable disable features',
        required_permissions: [organizationPermissionCodes.organizationManageFeatures],
    },
    {
        key: 'reference-data',
        label: 'reference data',
        children: [
            {key: 'organization-categories', label: 'organization categories'},
            {
                key: 'medal-group',
                label: 'certifications',
                children: [
                    {key: 'medal-list', label: 'certificate list'},
                    {key: 'medal-rewards-history', label: 'award history'},
                ],
            },
        ],
    },
    {key: 'terminology', label: 'terminology & labels'},
    {key: 'delete', label: 'delete organization', owner_only: true},
]

// ─── Sub-items map (route key → sub-items) ───────────────────────────────────

const SUB_ITEMS_MAP = {
    [WorkSpaceActions.HOME]: HOME_SUB_ITEMS,
    [WorkSpaceActions.COMMUNICATIONS]: COMMUNICATIONS_SUB_ITEMS,
    [WorkSpaceActions.MEMBER]: MEMBER_SUB_ITEMS,
    [WorkSpaceActions.GAMES]: GAMES_SUB_ITEMS,
    [WorkSpaceActions.COURSE]: COURSE_SUB_ITEMS,
    [WorkSpaceActions.PRODUCT]: SHOP_SUB_ITEMS,
    [WorkSpaceActions.FINANCE]: FINANCE_SUB_ITEMS,
    [WorkSpaceActions.FAMILY_BOOK]: FAMILY_BOOK_SUB_ITEMS,
    [WorkSpaceActions.SETTING]: SETTING_SUB_ITEMS,
}

// ─── SectionTemplate ─────────────────────────────────────────────────────────

export const SectionTemplate = ({title, description, renderActionButton}) => (
    <div className="p-1 sm:p-2 w-full">
        <h1 className="text-xl font-bold text-primary uppercase mb-4">{title}</h1>
        <div className="p-4 background border border-color-50 shadow-lg rounded-xl">
            <p className="mb-2 text-base text-color-200 tracking-wider">
                {description}
            </p>
            {renderActionButton && (
                <div className="text-right">
                    {renderActionButton()}
                </div>
            )}
        </div>
    </div>
)

// ─── DisableBadge ──────────────────────────────────────────────────────────

const DisableBadge = () => (
    <Translate>
        {t => (
            <TooltipApp
                title={t('You need administrator permission to access this', {admin: ( <strong>{t('administrator')}</strong> )})}
                placement="right" overlayInnerStyle={{fontSize: 12, fontStyle: 'italic'}}>
                <span
                    style={{padding: '4px 8px', cursor: 'pointer'}}
                    className="flex flex-center gap-0.5 border border-secondary-400 bg-secondary-50 rounded-md leading-tight opacity-70 hover:opacity-100">
                    <HiLockClosed size={12} className="text-secondary-600"/>
                    <span className="font-medium text-2xs text-secondary-500 italic leading-none">
                        {t('restricted')}
                    </span>
                </span>
            </TooltipApp>
        )}
    </Translate>
)

// ─── WorkspaceSidebarItem ────────────────────────────────────────────────────

const WorkspaceSidebarItem = ({route, url, isActive, isDisabled, subItems = [], permissions}) => {
    const t = useTranslate()
    const hasChildren = !_.isEmpty(subItems)
    const [open, setOpen] = useState(isActive && hasChildren)

    useEffect(() => {
        if (isActive && hasChildren) setOpen(true)
    }, [isActive, hasChildren])

    const itemClass = classNames(
        'flex items-center gap-2 transition-all duration-300 no-underline relative rounded-md',
        isActive ? 'font-semibold bg-secondary-50 text-secondary-600' : 'text-slate-600 hover:background-200',
        isDisabled && 'opacity-40 pointer-events-none'
    )

    const handleClick = (e) => {
        if (hasChildren) {
            e.preventDefault()
            e.stopPropagation()
            setOpen(v => !v)
        }
    }

    return (
        <div className="flex flex-col gap-1 SidebarItem relative">
            {(isDisabled || !url) ? (
                <div style={{ padding: '0.35rem 0.55rem' }} className={itemClass}>
                    <SidebarItemInner
                        route={route}
                        isActive={isActive}
                        isDisabled={isDisabled}
                        hasChildren={hasChildren}
                        open={open}
                        onClick={handleClick}
                        t={t}
                    />
                </div>
            ) : (
                <NavLink to={url} style={{ padding: '0.35rem 0.55rem' }} className={itemClass}>
                    <SidebarItemInner
                        route={route}
                        isActive={isActive}
                        hasChildren={hasChildren}
                        open={open}
                        onClick={handleClick}
                        t={t}
                    />
                </NavLink>
            )}

            {(route.isUnderDevelopment || isDisabled) && (
                <div
                    style={{ top: '50%', transform: 'translate(0, -50%)' }}
                    className="absolute right-3">
                    {route.isUnderDevelopment ? <ComingSoonBadge/> : isDisabled && <DisableBadge />}
                </div>
            )}

            {hasChildren && open && (
                <div className="ml-5 my-1 pl-3 flex flex-col gap-1 border-l border-color-100">
                    {subItems.map(item => (
                        <WorkspaceSidebarSubItem
                            key={item.key}
                            item={item}
                            buildUrl={(key) => `${url}?rel=${key}`}
                            permissions={permissions}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

const SidebarItemInner = ({route, isActive, isDisabled, hasChildren, open, onClick, t}) => (
    <div onClick={onClick} className="flex items-center flex-1 gap-2">
        {isActive && (
            <div className="absolute left-0 h-5 border-l-3 border-secondary-500 rounded-tr-lg rounded-br-lg"/>
        )}
        <div
            style={{width: 25, height: 25}}
            className={classNames(
                'flex flex-center rounded-lg transition-all duration-300',
                isActive && 'bg-secondary-500 text-white'
            )}>
            <SearchGroupIcon
                type={route.key}
                size={isActive ? 15 : 17}
                className="flex-shrink-0"
                FallbackComponent={BsGrid}
            />
        </div>
        <span className="flex-1 tracking-wide leading-snug truncate">
            {t(route.label)}
        </span>
        {hasChildren && (
            <BsChevronRight
                size={14}
                style={{transform: open ? 'rotate(90deg)' : ''}}
                className="SidebarItemIcon transition-all duration-300"
            />
        )}
    </div>
)

// ─── WorkspaceOrgSwitcher ────────────────────────────────────────────────────

const WorkspaceOrgSwitcher = ({organization, type}) => {
    const t = useTranslate()
    return (
        <DrawDialog
            width={540}
            closable={true}
            title={
                <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                    {t('select organization')}
                </div>
            }
            component={
                <div
                    className="p-2 rounded-xl flex items-center gap-2 border border-color-50 hover:border-secondary-500 hover:shadow-md cursor-pointer"
                    style={{background: 'var(--background)'}}>
                    <Avatar
                        size={40}
                        icon={<BiSolidChurch size={20} className="text-primary-400"/>}
                        className="flex flex-center bg-primary-50 rounded-lg"
                    />
                    <div className="flex flex-col flex-1 gap-1">
                        {/* text-color-000: trước đây không có class màu nào ở đây, ăn theo màu chữ
                            mặc định của browser (đen) vì app không set color nền trên body - trong
                            .workspace-sidebar-dark (nền đà tối) bị lộ ra thành chữ đen khó đọc. */}
                        <span className="font-bold tracking-wide leading-tight max-lines-1 text-color-000">
                            {getTitle(organization)}
                        </span>
                        {renderIf(
                            type,
                            <div className="font-medium text-2xs text-blue-500 tracking-wider leading-tight">
                                {t(type)}
                            </div>
                        )}
                    </div>
                    <BsChevronDown size={12} className="text-color-300 transition-all duration-300"/>
                </div>
            }>
            {(isToggle, toggle) => isToggle && (
                <AdminOrganizations orgId={getId(organization)} onClose={toggle}/>
            )}
        </DrawDialog>
    )
}

// ─── Workspace ───────────────────────────────────────────────────────────────

const Workspace = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)
    const isReady = _.has(organization, 'available_actions')
    const permissions = useContext(PermissionContext)
    const {path: root} = useRouteMatch()
    const location = useLocation()
    const {org_type} = organization ?? {}
    const {org_types = {}} = useAppConfig('org_types')
    const type = _.get(org_types, org_type)

    const getPath = useCallback(
        (key) => [root.replace(/\/$/, ''), key].join('/'),
        [root]
    )

    const currentKey = useMemo(
        () => location?.pathname?.split('/').filter(Boolean).pop(),
        [location?.pathname]
    )

    const menu = useMemo(() => (
            <FlyWrapper top={0}>
                <div className="p-3 flex flex-col gap-1 min-h-screen workspace-sidebar-dark">
                <div className="px-2 flex flex-col gap-1">
                    {/* Org switcher */}
                    <label className="font-medium text-xs text-gray-500 tracking-widest uppercase">
                        {t('choose organization')}
                    </label>
                    <WorkspaceOrgSwitcher organization={organization} type={type}/>
                </div>

                <div className="my-2 border-b border-color-100"/>

                {/* Nav items - route thuộc 1 module đang TẮT (owner có thể bật lại ở "Bật/tắt
                    chức năng") bị ẩn hẳn khỏi sidebar, không chỉ disable + badge như thiếu quyền. */}
                <div className="flex flex-col workspace-menu-sidebar">
                    {routes
                        .filter((route) => isModuleEnabledForRoute(route, organization))
                        .map((route) => {
                        const isDisabled = !hasPermissions(permissions, route.required_permissions) || route?.isUnderDevelopment
                        const url = getPath(route.key)
                        const isActive = currentKey === route.key

                        return (
                            <WorkspaceSidebarItem
                                key={route.key}
                                route={route}
                                url={url}
                                isActive={isActive}
                                isDisabled={isDisabled}
                                subItems={filterDevItems(SUB_ITEMS_MAP[route.key] ?? [])}
                                permissions={permissions}
                            />
                        )
                    })}
                </div>

                <div className="h-16"/>
                </div>
            </FlyWrapper>
    ), [permissions, organization, currentKey, getPath, type])

    return (
        <div className="flex flex-1">
            <ContentLayout
                title={null}
                renderHeader={NullAsDefault}
                menu={menu}
                renderRight={NullAsDefault}>
                {/* workspace-content - hook CSS riêng cho toàn bộ khu Workspace/admin (xem
                    tailwind_custom.css, mục 21) để nút context-menu (.contextIcon, dùng chung cho cả
                    feed/danh bạ công khai) LUÔN hiển thị ở đây - KHÔNG đổi rule .contextIcon dùng
                    chung, chỉ override riêng trong phạm vi class này (bạn báo "nhiều khi không biết
                    nó tồn tại" vì ẩn tới lúc hover, chỉ áp dụng cho khu Workspace, không đụng feed/
                    danh bạ công khai nơi ẩn-chờ-hover có thể là chủ ý). */}
                <div className="workspace-content flex-1 min-w-0">
                    {isReady ? (
                        <WorkspaceFeatures
                            activeKey={t(_.find(routes, {key: currentKey})?.label)}
                            routes={routes}
                            isDisabled={({required_permissions}) =>
                                !hasPermissions(permissions, required_permissions)
                            }
                            defaultAction={WorkSpaceActions.HOME}
                        />
                    ) : (
                        <LoadingBlock/>
                    )}
                </div>
            </ContentLayout>
        </div>
    )
}

// ─── Export ──────────────────────────────────────────────────────────────────

export default compose(
    connect((state) => {
        const login = getLogin(state)
        const primary_org = _.get(login, 'primary_org')
        return {
            primary_org,
            [permissionCodes.canEdit]: Boolean(!_.isEmpty(primary_org)),
        }
    }),
    branch(
        ({primary_org}) => _.isEmpty(primary_org),
        () => () => <WorkspaceLandingPage/>
    ),
    branch(
        (props) => !_.get(props, permissionCodes.canEdit),
        () => () => <Redirect to={staticPaths.start}/>
    )
)(nest(WorkspaceProvider, Workspace))